import { useMemo } from 'react';
import { useMutation, useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { CustomerOrder, CustomerOrderStatus } from 'src/types/CustomerOrder';
import { DeviceContractType } from 'src/types/DeviceContract';
import { CustomerOrderItem } from 'src/types/CustomerOrderItem';
import { useAggregatedQueries } from 'src/hooks/useAggregatedQuery';
import { get, post, put } from '../utils';
import { getPage, Page, PageRequest, sortValueFrom } from '../pagination';
import { getCustomerOrderItems } from './customer-order-item';
import useLog from 'src/hooks/useLog';
import useCustomerOrderData from 'src/hooks/useCustomerOrderData';
import { useQueryCustomersById } from './customer';

export function getCustomerOrderById(orderId: string): Promise<CustomerOrder> {
  return get<CustomerOrder>(`customerorder/${orderId}`);
}

export function getCustomerOrders(props: {
  status?: CustomerOrderStatus;
  customerId?: string;
  textQuery?: string;
}): Promise<CustomerOrder[]> {
  const { status, customerId, textQuery } = props;
  const params = {
    status: status && status.toString(),
    customerId,
    textQuery,
  };
  return get<CustomerOrder[]>('customerorder', params);
}

export function createCustomerOrder(
  customerId: string,
  uniqueOrderReference?: string,
  details?: string,
  status?: string,
  notes?: string,
  bundledSubscriptionMonths?: number
): Promise<CustomerOrder> {
  const body = {
    customerId,
    uniqueOrderReference,
    details,
    status,
    notes,
    bundledSubscriptionMonths,
  };
  return post<CustomerOrder>('customerorder', body);
}

export function sendOrderReminderToQuickCircuit(orderId: string): Promise<void> {
  return post<void>(`CustomerOrder/${orderId}/reminder`, {});
}

export function sendOrderNotificationToQuickCircuit(orderId: string): Promise<void> {
  return post<void>(`CustomerOrder/${orderId}/orderNotification`, {});
}

interface GenerateDeviceContractOptions {
  startsAt?: string;
  expiresAt?: string;
  contractType?: DeviceContractType;
  billAttachedDevices?: boolean;
  bundledSubscriptionMonths?: number;
}

export function generateDeviceContractsFromOrderId(orderId: string, options?: GenerateDeviceContractOptions): Promise<void> {
  return post<void>(`CustomerOrder/${orderId}/generateDeviceContracts`, {
    ...options,
  });
}

export function updateCustomerOrder(orderId: string, properties: object) {
  const body = {
    id: orderId,
    ...properties,
  };
  return put<CustomerOrder>(`customerorder/${orderId}`, body);
}

export function useSendCustomerOrderAcknowledgementEmailMutation() {
  const log = useLog();
  const { updateOrder } = useCustomerOrderData();

  return useMutation(({orderId}: { orderId: string }) => {
    return post<CustomerOrder>(`customerorder/${orderId}/orderAcknowledgement`);
  }, {
    onSuccess: (updatedOrder) => {
      updateOrder(updatedOrder);
      log.success('Order Acknowledgement Email successfully sent.');
    },
    onError: () => {
      log.error('Failed to send Order Acknowledgement Email.');
    }
  });
}

export function useQueryCustomerOrders(
  props: {
    status?: CustomerOrderStatus;
    customerId?: string;
    textQuery?: string;
  },
  options?: UseQueryOptions<CustomerOrder[], Error>
): UseQueryResult<CustomerOrder[]> {
  return useQuery(['CustomerOrder', props.status, props.customerId, props.textQuery], () => getCustomerOrders(props), options);
}

/** Sort fields the order search accepts. Mirrors the API's CUSTOMER_ORDER_SORT_FIELDS. */
export const CUSTOMER_ORDER_SORT_FIELDS = ['createdAt', 'status', 'uniqueOrderReference'] as const;

/**
 * How every paged orders card asks for its page: twenty rows, newest first, sortable by
 * the three columns the API knows.
 *
 * Shared for the same reason the columns are: two cards sitting on one screen under the
 * same headings must not disagree about what "first" means. Each card supplies only its
 * own `resetKey`.
 */
export const CUSTOMER_ORDER_TABLE_PAGING = {
  initialRowsPerPage: 20,
  initialSortField: 'createdAt',
  initialSortDesc: true,
  sortableFields: CUSTOMER_ORDER_SORT_FIELDS,
  /**
   * 100 and 200 are NOT offered here, and the cap is the mechanism rather than the
   * intention.
   *
   * While ORG_SCALE_PAGINATION is off the response is the legacy array with no
   * `customerName` and no `deviceCount`, so {@link useEnrichedCustomerOrderPage} resolves
   * both PER ROW — up to one items request per order plus one customer request per distinct
   * customer. At the 200 the generic footer offers that is up to 400 requests for one card,
   * on a screen that renders two of them: the request storm this whole change exists to
   * remove, one dropdown selection away. 50 bounds it at 100 per card.
   *
   * A conditional cap — full range once the server is paging — was the obvious alternative
   * and is worse: whether the response is enriched is only known AFTER a page has been
   * fetched at the chosen size, so the first 200-row burst happens either way and the
   * option set would shrink under the operator's cursor.
   */
  rowsPerPageOptions: [20, 50],
};

/**
 * An order row as the orders TABLES need it: with the customer's name and the number of
 * devices already on it.
 *
 * Both used to be resolved per row — one items request per order and one customer request
 * per customer — on a list that was itself unbounded and rendered twice on the same page.
 */
export interface CustomerOrderListItem extends CustomerOrder {
  customerName?: string;
  /**
   * Optional because on the flag-off branch it is RESOLVED rather than returned, and that
   * resolution can be pending or can have failed. `0` is a real answer to "how many devices"
   * and must not be how either of those two states renders.
   */
  deviceCount?: number;
}

/** How a row is ordered by each of {@link CUSTOMER_ORDER_SORT_FIELDS}. */
export const getCustomerOrderSortValue = sortValueFrom<CustomerOrderListItem>({
  createdAt: (order) => order.createdAt,
  status: (order) => order.status,
  uniqueOrderReference: (order) => order.uniqueOrderReference,
});

export interface CustomerOrderPageFilters {
  /** One status, or several — `?status=A&status=B` is one query, not two requests. */
  status?: CustomerOrderStatus | CustomerOrderStatus[];
  customerId?: string;
  textQuery?: string;
  /** ISO8601. Applied in SQL, not by filtering a full download in the browser. */
  createdAfter?: string;
}

/**
 * ONE page of customer orders, with the total and both derived columns.
 *
 * What this replaces, on a page that rendered it twice: a whole-table request PER STATUS,
 * then one `getCustomerOrderItems` per order and one `getCustomerById` per customer, and
 * "Recently Completed" pulling every COMPLETE order ever recorded to filter it to six
 * months client-side. On a real dataset that is thousands of requests and the page dies.
 */
export function getCustomerOrdersPage(
  filters: CustomerOrderPageFilters,
  request: PageRequest
): Promise<Page<CustomerOrderListItem>> {

  // The free-text term goes out as `textQuery` as well as `search`.
  //
  // They are the same term to two different server branches. The PAGED branch reads
  // `search`; the LEGACY branch — which is what answers while ORG_SCALE_PAGINATION is off
  // — has never heard of it and matches `textQuery` across order reference, details, notes
  // AND the customer's name. Sending only `search` left the flag-off search box unable to
  // match a customer name at all, because the name is not on the row at that point.
  //
  // A caller that already has its own textQuery (the Order Search card) keeps it: that
  // card owns the term and the table's box is switched off there.
  const textQuery = filters.textQuery ?? request.search;

  // AND IT TRAVELS AS `textQuery` ONLY — `search` is deliberately not forwarded.
  //
  // Both server branches match the same four fields through `buildOrderSearchWhere`:
  // customer name, order reference, details and notes. The paged branch reads
  // `textQuery || search`, so sending the term once under `textQuery` covers both.
  //
  // Sending it under BOTH names filtered the result TWICE — once in SQL, then again here,
  // because `getPage` client-side-filters whenever `request.search` is set. The second pass
  // ran against `searchable` below, which cannot see `customerName` (absent from the legacy
  // row) or `notes` (absent from the row type). So while ORG_SCALE_PAGINATION is off, an
  // order the server had correctly matched on the customer's name or on its notes was
  // dropped on arrival, and the card reported no results for a search that had them.
  const { search: _termGoesAsTextQuery, ...paging } = request;

  return getPage<CustomerOrderListItem>(
    'customerorder',
    paging,
    {
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.customerId ? { customerId: filters.customerId } : {}),
      ...(textQuery ? { textQuery } : {}),
      ...(filters.createdAfter ? { createdAfter: filters.createdAfter } : {}),
    },
    {
      // NO `searchable`, because nothing reaches it: the term goes out as `textQuery` and
      // the SERVER matches it on both branches, so `getPage` is never handed a `search` to
      // filter on. That is the point — a client-side matcher here can only ever be a
      // NARROWER copy of the server's, and it was: it could not see `customerName` (not on
      // the legacy row until useEnrichedCustomerOrderPage fills it in, afterwards) or
      // `notes` (not on the row type at all), so it deleted real matches from a result set
      // the database had already got right.
      //
      // Leaving it absent is also the guard: if a caller ever does pass `search` through,
      // `getPage` throws rather than silently presenting unfiltered rows as results.
      //
      // The sort still belongs here — the legacy branch knows no sort at all, so without
      // this a column header would paint a sort indicator over rows nobody ordered.
      sortValue: getCustomerOrderSortValue,
    }
  );
}

/**
 * One page of customer orders, as a query.
 *
 * On the FLAG-OFF branch the response is the legacy array, which carries neither
 * `customerName` nor `deviceCount` — see {@link useEnrichedCustomerOrderPage} for how the
 * tables fill those in without reintroducing the per-row fan-out at scale.
 */
export function useCustomerOrdersPage(
  filters: CustomerOrderPageFilters,
  request: PageRequest,
  options?: { enabled?: boolean; refetchInterval?: number }
): UseQueryResult<Page<CustomerOrderListItem>, Error> {
  return useQuery<Page<CustomerOrderListItem>, Error>(
    ['CustomerOrderPage', filters, request],
    () => getCustomerOrdersPage(filters, request),
    {
      enabled: options?.enabled ?? true,
      refetchInterval: options?.refetchInterval,
      // The rows stay while the next page loads, so paging dims the table instead of
      // emptying it.
      keepPreviousData: true,
    }
  );
}

/**
 * The sku prefix that marks a line as a SUBSCRIPTION rather than a device. Matches the
 * API's SUBSCRIPTION_SKU_PREFIX.
 */
const SUBSCRIPTION_SKU_PREFIX = 'SUB';

export function useQueryCustomerOrderDeviceCounts(orderIds: string[]) {
  return useAggregatedQueries<CustomerOrderItem[], Error, { id: string; count: number }>(
    orderIds.map((orderId) => ({
      queryKey: ['CustomerOrderDeviceCount', orderId],
      queryFn: () => getCustomerOrderItems(orderId),
      select: (data: CustomerOrderItem[]) => ({
        id: orderId,
        // The SUM of itemQuantity, not the number of lines. The two client-side
        // implementations of the "Number of Devices" column DISAGREED: the orders table
        // summed quantities and this one counted rows, so an order with a single line of
        // quantity 12 read as 12 in one table and 1 in the other, under the same heading.
        // The sum is the one that matches the heading, and it is what the API now returns.
        count: data
          .filter((item) => !item.sku?.startsWith(SUBSCRIPTION_SKU_PREFIX))
          .reduce((total, item) => total + (item.itemQuantity ?? 0), 0),
      }),
    }))
  );
}

/**
 * Fill in `customerName` and `deviceCount` for a page the SERVER did not enrich.
 *
 * On the paged branch the API sends both and this does nothing. On the FLAG-OFF branch the
 * response is the legacy array with neither, so they are resolved here — but over the
 * PAGE, which getPage has already windowed to at most the rows on screen. That is the same
 * per-row fan-out these tables used to do, bounded to the page instead of to every order
 * in the system, and it disappears entirely once ORG_SCALE_PAGINATION is on.
 *
 * The honest bound: up to one items request per row plus one customer request per distinct
 * customer. {@link CUSTOMER_ORDER_TABLE_PAGING} therefore starts at 20 AND offers no page
 * size above 50, which bounds one card at ~100 requests instead of the ~400 the generic
 * footer's 200 would allow — on a screen that renders two of these cards. Stated rather than
 * implied, because an unstated bound is how the storm this replaced grew in the first place;
 * for a while this paragraph asserted a cap that no code applied.
 */
export function useEnrichedCustomerOrderPage(page: Page<CustomerOrderListItem> | undefined) {
  const items = useMemo(() => page?.items ?? [], [page]);
  const needsEnrichment = Boolean(page) && !page?.serverPaged;

  const customerIds = useMemo(
    () => (needsEnrichment ? [...new Set(items.map((order) => order.customerId).filter(Boolean))] : []),
    [needsEnrichment, items]
  );
  const orderIds = useMemo(
    () => (needsEnrichment ? items.map((order) => order.id) : []),
    [needsEnrichment, items]
  );

  const { data: customers = [], isLoading: customersLoading } = useQueryCustomersById(customerIds);
  const { data: deviceCounts = [], isLoading: deviceCountsLoading } = useQueryCustomerOrderDeviceCounts(orderIds);

  const rows = useMemo(() => {
    if (!needsEnrichment) {
      return items;
    }
    return items.map((order) => ({
      ...order,
      // `?.` on BOTH, and it is not defensive noise: useAggregatedQueries returns
      // `results.map((r) => r.data)`, so every entry is `undefined` until its query
      // resolves — which is the state of the very first render after the page lands, and
      // the permanent state of any entry whose request failed. Reading `.id` off it threw
      // during render, with no error boundary above it: a white screen on /orders and
      // /quickcircuit/orders rather than a missing number in one cell.
      customerName: customers.find((customer) => customer?.id === order.customerId)?.name,
      // NO `?? 0`. useAggregatedQueries returns `results.map((r) => r.data)`, so an entry is
      // undefined both while its request is in flight and permanently once it has FAILED —
      // and defaulting that to zero told the shipping desk an order contained no devices when
      // the count simply had not loaded. Left undefined, so the column can say which it is.
      deviceCount: deviceCounts.find((count) => count?.id === order.id)?.count,
    }));
  }, [needsEnrichment, items, customers, deviceCounts]);

  return {
    rows,
    enriching: needsEnrichment && (customersLoading || deviceCountsLoading),
  };
}

export function useQueryCustomerOrderById(orderId: string): UseQueryResult<CustomerOrder> {
  return useQuery(['CustomerOrder', orderId], () => getCustomerOrderById(orderId));
}

export function useQueryCustomerOrdersById(orderIds: string[]) {
  return useAggregatedQueries<CustomerOrder>(
    orderIds.map((orderId) => ({
      queryKey: ['CustomerOrder', orderId],
      queryFn: () => getCustomerOrderById(orderId),
    }))
  );
}
